/**
 * 虛擬商品（純數位） — 對應 Categories #06 virtual。
 * 對應 API: GET /api/virtual-products
 *
 * 不同於實體商品：沒有重量/長度/材質/運費；購買後 email 啟用碼，登入機台綁定 Player ID。
 */

export const VIRTUAL_MACHINES = {
  D2:    'D2',     // DARTSLIVE 2
  D3:    'D3',     // DARTSLIVE 3
  FUN:   'FUN',    // Fun Dart (遊戲擴充)
  D2_D3: 'D2_D3',  // 同時相容 DARTSLIVE 2 與 3
};

export const VIRTUAL_MACHINE_LABELS = {
  [VIRTUAL_MACHINES.D2]:    'DARTSLIVE 2',
  [VIRTUAL_MACHINES.D3]:    'DARTSLIVE 3',
  [VIRTUAL_MACHINES.FUN]:   'FUN DART',
  [VIRTUAL_MACHINES.D2_D3]: 'DARTSLIVE 2 & 3',
};

export const VIRTUAL_VALIDITY = {
  PERMANENT: 'permanent',  // 永久使用
  SEASONAL:  'seasonal',   // 期間限定
};

export const VIRTUAL_VALIDITY_LABELS = {
  [VIRTUAL_VALIDITY.PERMANENT]: '永久使用',
  [VIRTUAL_VALIDITY.SEASONAL]:  '期間限定',
};

export const VIRTUAL_CATEGORIES = {
  THEME:        'theme',          // 主題卡
  ANIMATION:    'animation',      // 動畫主題
  SEASONAL:     'seasonal_theme', // 季節主題
  EFFECT:       'effect',         // 特效主題
  THEME_SOUND:  'theme_sound',    // 主題 + 音效
  GAME_UNLOCK:  'game_unlock',    // 遊戲解鎖
};

export const VIRTUAL_CATEGORY_LABELS = {
  [VIRTUAL_CATEGORIES.THEME]:       '主題卡 THEME',
  [VIRTUAL_CATEGORIES.ANIMATION]:   '動畫主題',
  [VIRTUAL_CATEGORIES.SEASONAL]:    '季節主題',
  [VIRTUAL_CATEGORIES.EFFECT]:      '特效主題',
  [VIRTUAL_CATEGORIES.THEME_SOUND]: '主題 + 音效',
  [VIRTUAL_CATEGORIES.GAME_UNLOCK]: '遊戲解鎖',
};

// Google open-source sample videos — replace with /uploads/{id}-preview.mp4 in production
const SAMPLE_VIDEOS = [
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/SubaruOutbackOnStreetAndDirt.mp4',
];

export const VIRTUAL_PRODUCTS = [
  {
    id: 'aurora',
    name: '極光主題 AURORA',
    machine: VIRTUAL_MACHINES.D3,
    category: VIRTUAL_CATEGORIES.THEME,
    validity: VIRTUAL_VALIDITY.PERMANENT,
    tags: ['new'],
    price: 280,
    unpublishAt: '2099-12-31T23:59:00+08:00',
    shareToken: 'countdown-demo',
    // 變體（種類/角色）：各自對應金額。之後改由後台設定。
    // 沒有 variants 的商品，登記時會 fallback 成單一「標準」。
    variants: [
      { id: 'roleA', name: '角色A', price: 280 },
      { id: 'roleB', name: '角色B', price: 320 },
      { id: 'roleC', name: '角色C', price: 360 },
    ],
    // 多張商品圖（第一張 = 列表縮圖），詳情頁圖庫展示用
    images: [
      'https://picsum.photos/seed/aurora-1/800/500',
      'https://picsum.photos/seed/aurora-2/800/500',
      'https://picsum.photos/seed/aurora-3/800/500',
    ],
    previewUrl: SAMPLE_VIDEOS[0],
  },
  {
    id: 'neon-tokyo',
    name: '霓虹東京 NEON TOKYO',
    machine: VIRTUAL_MACHINES.D3,
    category: VIRTUAL_CATEGORIES.ANIMATION,
    validity: VIRTUAL_VALIDITY.PERMANENT,
    price: 350,
    variants: [
      { id: 'd3', name: 'DARTSLIVE 3 版', price: 350 },
      { id: 'd2', name: 'DARTSLIVE 2 版', price: 300 },
    ],
    images: [
      'https://picsum.photos/seed/neon-1/800/500',
      'https://picsum.photos/seed/neon-2/800/500',
    ],
    previewUrl: SAMPLE_VIDEOS[1],
  },
  {
    id: 'sakura-2025',
    name: '櫻花季限定 SAKURA',
    machine: VIRTUAL_MACHINES.D2,
    category: VIRTUAL_CATEGORIES.SEASONAL,
    validity: VIRTUAL_VALIDITY.SEASONAL,
    tags: ['limited'],
    price: 180,
    unpublishAt: '2025-03-31T23:59:00+08:00',
    shareToken: 'expired-demo',
    previewUrl: SAMPLE_VIDEOS[2],
  },
  {
    id: 'cyber',
    name: '網絡駭客 CYBER',
    machine: VIRTUAL_MACHINES.D3,
    category: VIRTUAL_CATEGORIES.EFFECT,
    validity: VIRTUAL_VALIDITY.PERMANENT,
    price: 320,
    previewUrl: SAMPLE_VIDEOS[3],
  },
  {
    id: 'koi',
    name: '錦鯉躍龍門 KOI',
    machine: VIRTUAL_MACHINES.D2,
    category: VIRTUAL_CATEGORIES.THEME_SOUND,
    validity: VIRTUAL_VALIDITY.PERMANENT,
    price: 380,
    previewUrl: SAMPLE_VIDEOS[4],
  },
  {
    id: 'fun-monopoly',
    name: 'Fun Dart · 大富翁模式',
    machine: VIRTUAL_MACHINES.FUN,
    category: VIRTUAL_CATEGORIES.GAME_UNLOCK,
    validity: VIRTUAL_VALIDITY.PERMANENT,
    price: 420,
    previewUrl: SAMPLE_VIDEOS[5],
  },
  {
    id: 'galaxy',
    name: '無垠星河 GALAXY',
    machine: VIRTUAL_MACHINES.D3,
    category: VIRTUAL_CATEGORIES.ANIMATION,
    validity: VIRTUAL_VALIDITY.PERMANENT,
    price: 360,
    previewUrl: SAMPLE_VIDEOS[6],
  },
  {
    id: 'house',
    name: '合掌村 WAKAYAMA',
    machine: VIRTUAL_MACHINES.D3,
    category: VIRTUAL_CATEGORIES.THEME,
    validity: VIRTUAL_VALIDITY.PERMANENT,
    tags: ['new'],
    price: 320,
    previewUrl: SAMPLE_VIDEOS[7],
  },
  {
    id: 'matsuri',
    name: '夏祭 MATSURI',
    machine: VIRTUAL_MACHINES.D2,
    category: VIRTUAL_CATEGORIES.THEME_SOUND,
    validity: VIRTUAL_VALIDITY.SEASONAL,
    tags: ['limited'],
    price: 240,
    previewUrl: SAMPLE_VIDEOS[8],
  },
];
